import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DatePipe } from '@angular/common';

import { environment } from '@env/environment';
import { AuthQuery } from '@app/auth/auth.query';
import { StammdatenService, Stammdaten } from '@app/services/stammdaten.service';
import { TransferMandatService, NewTransferItem } from '@app/services/transfer.service';

@Component({
  selector: 'app-new-item-wizard',
  templateUrl: './new-item-wizard.component.html',
  styleUrls: ['./new-item-wizard.component.scss'],
})
export class NewItemWizardComponent implements OnInit {
  // German input formats
  static readonly DATE_PATTERN = /^\d{2}\.\d{2}\.\d{4}$/;
  static readonly TIME_PATTERN = /^\d{1,2}:\d{2}$/;

  // wizard step: 1 = type, 2 = info, 3 = images, 4 = summary
  step = 1;

  // 0 = Anzeige, 1 = BOM
  bom = 0;

  frm: FormGroup;

  // reserved before the image step so uploads can be linked to it
  aktenzeichen = '';
  reserving = false;
  saving = false;

  uploadedImages = 0;

  stammdaten: Stammdaten = {
    farben: [],
    hersteller: [],
    streets: [],
    bezirke: [],
    lander: [],
    tatbestande: [],
    usage: 0,
  };

  pondFiles: any[] = [];
  pondOptions = {
    class: 'my-filepond',
    credits: false,
    multiple: true,
    maxFiles: 5,
    allowFileTypeValidation: true,
    acceptedFileTypes: ['image/png', 'image/jpeg'],
    labelFileTypeNotAllowed: 'File of invalid type: only JPG, PNG',
    fileValidateTypeLabelExpectedTypesMap: { 'image/jpeg': '.jpg', 'image/png': '.png' },
    allowImageResize: true,
    imageResizeTargetWidth: 2400,
    imageResizeTargetHeight: 1600,
    imageResizeMode: 'contain',
    imageResizeUpscale: true,
    labelIdle: 'Bilder hier ablegen oder <span class="filepond--label-action">durchsuchen</span>',
    server: {
      process: (fieldName, file, metadata, load, error, progress, abort) => {
        const formData = new FormData();
        formData.append(fieldName, file, file.name);
        formData.append('filepond', file.name);

        formData.append('uid', '' + this.aktenzeichen);
        formData.append('fid', '' + this.aktenzeichen);
        formData.append('role', '3');
        formData.append('imgtype', '' + fieldName);

        formData.append('aktenzeichen', '' + this.aktenzeichen);
        formData.append('did', '' + this.authQuery.getValue().dienstnr);
        formData.append('didstvo', '' + this.authQuery.getValue().dienstnr_stvo);
        formData.append('didname', '' + this.authQuery.getValue().dienstname);

        formData.append('apikey', '' + environment.api_key);
        formData.append('phone_id', '' + this.authQuery.getValue().phone_id);
        formData.append('token', '' + this.authQuery.getValue().access_token);
        formData.append('akey', '' + environment.api_key);

        const request = new XMLHttpRequest();
        request.open('POST', environment.serverUrl + '/filepond/process');
        request.setRequestHeader('Access-Control-Allow-Origin', '*');
        request.setRequestHeader('Access-Control-Allow-Headers', '*');
        request.setRequestHeader('Authorization', `Bearer ${this.authQuery.access_token}`);

        request.upload.onprogress = (e) => {
          progress(e.lengthComputable, e.loaded, e.total);
        };

        request.onload = () => {
          if (request.status >= 200 && request.status < 300) {
            load(request.responseText);
          } else {
            error('upload failed');
          }
        };

        request.send(formData);

        return {
          abort: () => {
            request.abort();
            abort();
          },
        };
      },
      revert: 'filepond/revert',
      restore: 'filepond/restore?id=',
      fetch: 'filepond/fetch?data=',
      load: 'filepond/load',
    },
  };

  constructor(
    private _fb: FormBuilder,
    private dialogRef: MatDialogRef<NewItemWizardComponent>,
    private _snackBar: MatSnackBar,
    private datePipe: DatePipe,
    private authQuery: AuthQuery,
    private stammdatenService: StammdatenService,
    private transferService: TransferMandatService
  ) {
    // German formats: date = TT.MM.JJJJ, time = HH:MM (24h)
    const today = this.datePipe.transform(new Date(), 'dd.MM.yyyy') || '';
    const now = this.datePipe.transform(new Date(), 'HH:mm') || '';

    this.frm = this._fb.group({
      kzland: ['A', Validators.required],
      kzbezirk: [''],
      kznummer: ['', Validators.required],
      street: ['', Validators.required],
      streetnbr: [''],
      hersteller: [''],
      farbe: [''],
      tat_datum: [today, [Validators.required, Validators.pattern(NewItemWizardComponent.DATE_PATTERN)]],
      tat_zeit: [now, [Validators.required, Validators.pattern(NewItemWizardComponent.TIME_PATTERN)]],
      tatzeit_bis_datum: [''],
      tatzeit_bis_uhrzeit: [''],
    });
  }

  ngOnInit(): void {
    this.stammdatenService.getStammdaten().subscribe(
      (response) => {
        const d = response.data;
        d.lander = this.sortByBezeichnung(d.lander);
        d.bezirke = this.sortByBezeichnung(d.bezirke);
        d.streets = this.sortByBezeichnung(d.streets);
        d.hersteller = this.sortByBezeichnung(d.hersteller);
        d.farben = this.sortByBezeichnung(d.farben);
        this.stammdaten = d;
      },
      () => {
        // non-fatal: dropdowns stay empty, fields can still be typed/skipped
      }
    );
  }

  private sortByBezeichnung<T extends { bezeichnung: string }>(arr: T[]): T[] {
    if (!Array.isArray(arr)) {
      return [];
    }
    return [...arr].sort((a, b) =>
      ('' + (a?.bezeichnung ?? '')).localeCompare('' + (b?.bezeichnung ?? ''), 'de')
    );
  }

  selectType(bom: number): void {
    this.bom = bom;
    this.applyBomValidators();
  }

  private applyBomValidators(): void {
    const bisDatum = this.frm.get('tatzeit_bis_datum');
    const bisZeit = this.frm.get('tatzeit_bis_uhrzeit');
    if (this.bom === 1) {
      bisDatum?.setValidators([Validators.required, Validators.pattern(NewItemWizardComponent.DATE_PATTERN)]);
      bisZeit?.setValidators([Validators.required, Validators.pattern(NewItemWizardComponent.TIME_PATTERN)]);
    } else {
      bisDatum?.clearValidators();
      bisZeit?.clearValidators();
    }
    bisDatum?.updateValueAndValidity();
    bisZeit?.updateValueAndValidity();
  }

  get typeLabel(): string {
    return this.bom === 1 ? 'BOM' : 'Anzeige';
  }

  // ---- navigation ----

  goToInfo(): void {
    this.step = 2;
  }

  // step 2 -> reserve aktenzeichen, then go to images
  reserveAndContinue(): void {
    if (this.frm.invalid) {
      this.frm.markAllAsTouched();
      this._snackBar.open('Bitte alle Pflichtfelder ausfüllen.', '', { duration: 3000 });
      return;
    }

    // already reserved (e.g. navigating back & forth) -> skip
    if (this.aktenzeichen) {
      this.step = 3;
      return;
    }

    this.reserving = true;
    this.transferService.getNextAktenzeichen(this.bom === 1).subscribe(
      (res) => {
        this.reserving = false;
        const next = res?.next;
        if (res?.result === 1 && next !== undefined && next !== null && '' + next !== '') {
          this.aktenzeichen = '' + next;
          this.step = 3;
        } else {
          this._snackBar.open('Aktenzeichen konnte nicht ermittelt werden.', '', { duration: 4000 });
        }
      },
      () => {
        this.reserving = false;
        this._snackBar.open('Aktenzeichen konnte nicht ermittelt werden.', '', { duration: 4000 });
      }
    );
  }

  goToSummary(): void {
    this.step = 4;
  }

  back(): void {
    if (this.step > 1) {
      this.step = this.step - 1;
    }
  }

  // ---- filepond events ----

  onPondProcess(event: any): void {
    if (!event?.error) {
      this.uploadedImages = this.uploadedImages + 1;
    }
  }

  onPondRemove(event: any): void {
    if (this.uploadedImages > 0) {
      this.uploadedImages = this.uploadedImages - 1;
    }
  }

  // ---- save ----

  save(): void {
    if (this.saving) {
      return;
    }
    if (!this.aktenzeichen) {
      this._snackBar.open('Kein Aktenzeichen reserviert. Bitte zurück und erneut versuchen.', '', { duration: 4000 });
      return;
    }

    const v = this.frm.value;
    const item: NewTransferItem = {
      bom: this.bom,
      aktenzeichen: this.aktenzeichen,
      kzland: v.kzland,
      kzbezirk: v.kzbezirk,
      kznummer: v.kznummer,
      street: v.street,
      streetnbr: v.streetnbr,
      hersteller: v.hersteller,
      farbe: v.farbe,
      tat_datum: v.tat_datum,
      tat_zeit: v.tat_zeit,
      tatzeit_bis_datum: this.bom === 1 ? v.tatzeit_bis_datum : '',
      tatzeit_bis_uhrzeit: this.bom === 1 ? v.tatzeit_bis_uhrzeit : '',
    };

    this.saving = true;
    this.transferService.createNewItem(item).subscribe(
      (res) => {
        this.saving = false;
        if (res?.result === 1) {
          this._snackBar.open(`Neuer Eintrag (${this.typeLabel}) ${this.aktenzeichen} angelegt.`, '', {
            duration: 4000,
          });
          this.dialogRef.close({ created: true, aktenzeichen: this.aktenzeichen, bom: this.bom });
        } else {
          this._snackBar.open('Eintrag konnte nicht angelegt werden.', '', { duration: 4000 });
        }
      },
      () => {
        this.saving = false;
        this._snackBar.open('Eintrag konnte nicht angelegt werden.', '', { duration: 4000 });
      }
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
